import React, { useState, useRef } from 'react';
import '../styles/TemplateEditor.css';
import { notify } from './NotificationContainer';
import { prompt } from './PromptContainer';

const TemplateEditor = ({ template, variables, onSave, onClose }) => {
  const [formData, setFormData] = useState(template || {
    nom: '',
    description: '',
    sujet: '',
    corps: '',
    cc_emails: '',
    bcc_emails: '',
    actif: true,
    envoyer_automatiquement: false,
    evenement_declencheur: null
  });

  const [showVariables, setShowVariables] = useState(false);
  const [searchVariable, setSearchVariable] = useState('');
  const sujetRef = useRef(null);
  const corpsRef = useRef(null);

  const evenementChoices = [
    {
      value: 'PARENT_NOUVELLE_INSCRIPTION',
      label: 'Nouvelle inscription créée (notification parent)',
      description: 'Envoyé automatiquement au parent après qu\'il a créé une nouvelle inscription. Confirme la réception de sa demande et récapitule les informations de l\'inscription.'
    },
    {
      value: 'INSCRIPTION_CONFIRMEE',
      label: 'Inscription confirmée',
      description: 'Envoyé lorsqu\'une inscription est validée et confirmée par l\'administrateur. Le parent reçoit la confirmation officielle avec tous les détails du séjour et les informations financières.'
    },
    {
      value: 'INSCRIPTION_EN_ATTENTE',
      label: 'Inscription en attente',
      description: 'Envoyé automatiquement après qu\'un parent a soumis une demande d\'inscription. Informe que la demande a bien été reçue et qu\'elle est en cours d\'examen.'
    },
    {
      value: 'INSCRIPTION_ANNULEE',
      label: 'Inscription annulée',
      description: 'Envoyé lorsqu\'une inscription est annulée, que ce soit à la demande du parent ou par l\'administration. Notifie officiellement l\'annulation.'
    },
    {
      value: 'PAIEMENT_VALIDE',
      label: 'Paiement validé',
      description: 'Envoyé après la réception et la validation d\'un paiement. Confirme le montant reçu, le mode de paiement et met à jour la situation financière du dossier.'
    },
    {
      value: 'PAIEMENT_REFUSE',
      label: 'Paiement refusé',
      description: 'Envoyé lorsqu\'un paiement a échoué ou a été rejeté. Demande au parent de régulariser la situation et de nous contacter.'
    },
    {
      value: 'PAIEMENT_RAPPEL',
      label: 'Rappel de paiement',
      description: 'Envoyé pour rappeler au parent qu\'un solde reste à payer. Cet email doit être envoyé manuellement selon les besoins (pas d\'envoi automatique possible).'
    },
    {
      value: 'DOCUMENT_VALIDE',
      label: 'Document validé',
      description: 'Envoyé lorsque les documents fournis (fiche sanitaire, autorisation, etc.) ont été vérifiés et validés par l\'équipe administrative.'
    },
    {
      value: 'DOCUMENT_REFUSE',
      label: 'Document refusé',
      description: 'Envoyé lorsque des documents soumis sont non conformes, illisibles ou incomplets. Demande au parent de soumettre à nouveau les documents corrects.'
    },
    {
      value: 'DOCUMENT_MANQUANT',
      label: 'Document manquant',
      description: 'Envoyé pour rappeler au parent qu\'il manque des documents obligatoires pour finaliser le dossier d\'inscription (trousseau, fiche sanitaire, etc.).'
    },
    {
      value: 'AIDE_VALIDEE',
      label: 'Aide financière validée',
      description: 'Envoyé lorsqu\'une demande d\'aide financière (CAF, quotient familial, bourse) a été acceptée. Indique le montant de l\'aide accordée et recalcule le reste à charge.'
    },
    {
      value: 'AIDE_REFUSEE',
      label: 'Aide financière refusée',
      description: 'Envoyé lorsqu\'une demande d\'aide financière ne peut être acceptée. Explique que la demande a été examinée mais ne peut faire l\'objet d\'une suite favorable.'
    },
    {
      value: 'AIDE_ACCORD',
      label: 'Accord aide financière',
      description: 'Envoyé comme accusé de réception d\'une demande d\'aide financière. Informe que le dossier est en cours d\'examen et qu\'une réponse suivra.'
    },
    {
      value: 'AIDE_PAIEMENT',
      label: 'Paiement aide reçu',
      description: 'Envoyé lorsque le paiement de l\'aide financière (par exemple de la CAF ou d\'un partenaire) a été reçu et enregistré sur le dossier.'
    },
    {
      value: 'PARTENAIRE_VALIDE',
      label: 'Inscription partenaire validée',
      description: 'Envoyé lorsqu\'une inscription effectuée via un partenaire (CE, mairie, association) a été validée. Confirme la prise en charge partenaire.'
    },
    {
      value: 'PARTENAIRE_REFUSE',
      label: 'Inscription partenaire refusée',
      description: 'Envoyé lorsqu\'une inscription via partenaire ne peut être validée (quota atteint, conditions non remplies, etc.). Invite à nous contacter pour plus d\'informations.'
    },
    {
      value: 'ADMIN_NOUVELLE_INSCRIPTION',
      label: 'Nouvelle inscription (notification admin)',
      description: 'Notification automatique envoyée aux administrateurs lors de chaque nouvelle inscription d\'un parent (sans partenaire social). Permet aux admins d\'être informés immédiatement des nouvelles demandes.'
    },
    {
      value: 'ADMIN_INSCRIPTION_PARTENAIRE',
      label: 'Inscription via partenaire en attente (notification admin)',
      description: 'Notification automatique envoyée aux administrateurs lorsqu\'un parent inscrit un enfant via un partenaire social. Indique qu\'une validation du partenaire est requise.'
    },
    {
      value: 'SYSTEME_BIENVENUE_MEMBRE',
      label: 'Bienvenue nouveau membre (inscription compte)',
      description: 'Email automatique envoyé lors de la création d\'un nouveau compte parent. Souhaite la bienvenue et explique comment utiliser la plateforme. Variables disponibles : {user.prenom}, {user.nom}, {user.email}, {user.telephone}'
    },
    {
      value: 'SYSTEME_RESET_PASSWORD',
      label: 'Réinitialisation mot de passe',
      description: 'Email automatique envoyé lors d\'une demande de réinitialisation de mot de passe. Contient le lien de réinitialisation valable 24h. Variables disponibles : {user.prenom}, {user.nom}, {user.email}, {reset_url}'
    },
    {
      value: 'SYSTEME_TEST_SMTP',
      label: 'Email de test SMTP',
      description: 'Email de test pour vérifier la configuration SMTP. Affiche les informations de configuration. Variables disponibles : {smtp.host}, {smtp.port}, {smtp.username}, {smtp.from_email}, {smtp.from_name}'
    },
    {
      value: 'SYSTEME_NOTIFICATION_ADMIN',
      label: 'Notification générique admin',
      description: 'Email de notification générique pour les administrateurs. Peut être utilisé pour tout type de notification système personnalisée.'
    },
  ];

  const handleChange = (field, value) => {
    const newData = { ...formData, [field]: value };

    // Si on désactive l'envoi automatique, on efface l'événement
    if (field === 'envoyer_automatiquement' && !value) {
      newData.evenement_declencheur = null;
    }

    setFormData(newData);
  };

  const insertVariable = (variable, targetField) => {
    const variableText = `{${variable}}`;
    const ref = targetField === 'sujet' ? sujetRef : corpsRef;

    if (ref.current) {
      const start = ref.current.selectionStart;
      const end = ref.current.selectionEnd;
      const currentValue = formData[targetField] || '';
      const newValue =
        currentValue.substring(0, start) +
        variableText +
        currentValue.substring(end);

      handleChange(targetField, newValue);

      // Repositionner le curseur
      setTimeout(() => {
        ref.current.focus();
        ref.current.selectionStart = ref.current.selectionEnd =
          start + variableText.length;
      }, 0);
    }
  };

  const applyFormatting = async (format) => {
    const ref = corpsRef;
    if (!ref.current) return;

    const start = ref.current.selectionStart;
    const end = ref.current.selectionEnd;
    const selectedText = formData.corps.substring(start, end);

    if (!selectedText) {
      notify.warning('Veuillez sélectionner du texte d\'abord');
      return;
    }

    let formattedText = '';
    switch (format) {
      case 'bold':
        formattedText = `<strong>${selectedText}</strong>`;
        break;
      case 'italic':
        formattedText = `<em>${selectedText}</em>`;
        break;
      case 'underline':
        formattedText = `<u>${selectedText}</u>`;
        break;
      case 'h2':
        formattedText = `<h2>${selectedText}</h2>`;
        break;
      case 'h3':
        formattedText = `<h3>${selectedText}</h3>`;
        break;
      case 'paragraph':
        formattedText = `<p>${selectedText}</p>`;
        break;
      case 'link':
        const url = await prompt.askRequired('Entrez l\'URL :', 'https://exemple.com', 'Ajouter un lien');
        if (url) formattedText = `<a href="${url}">${selectedText}</a>`;
        else return;
        break;
      default:
        return;
    }

    const newValue =
      formData.corps.substring(0, start) +
      formattedText +
      formData.corps.substring(end);

    handleChange('corps', newValue);

    // Repositionner le curseur
    setTimeout(() => {
      ref.current.focus();
      ref.current.selectionStart = ref.current.selectionEnd = start + formattedText.length;
    }, 0);
  };

  const insertStructure = (structure) => {
    const ref = corpsRef;
    if (!ref.current) return;

    const start = ref.current.selectionStart;
    let insertText = '';

    switch (structure) {
      case 'salutation':
        insertText = 'Bonjour {parent.prenom},\n\n';
        break;
      case 'signature':
        insertText = '\n\nCordialement,\nL\'équipe Crions D\'Couleurs';
        break;
      case 'list':
        insertText = '<ul>\n  <li>Premier élément</li>\n  <li>Deuxième élément</li>\n</ul>';
        break;
      default:
        return;
    }

    const newValue =
      formData.corps.substring(0, start) +
      insertText +
      formData.corps.substring(start);

    handleChange('corps', newValue);

    setTimeout(() => {
      ref.current.focus();
      ref.current.selectionStart = ref.current.selectionEnd = start + insertText.length;
    }, 0);
  };

  const filteredVariables = Object.entries(variables).reduce((acc, [category, vars]) => {
    if (!searchVariable) return { ...acc, [category]: vars };

    const filtered = Object.entries(vars).filter(
      ([key, desc]) =>
        key.toLowerCase().includes(searchVariable.toLowerCase()) ||
        desc.toLowerCase().includes(searchVariable.toLowerCase())
    );

    if (filtered.length > 0) {
      acc[category] = Object.fromEntries(filtered);
    }

    return acc;
  }, {});

  const getDestinataireInfo = (evenement) => {
    const destinataireMap = {
      'INSCRIPTION_CONFIRMEE': 'Email du parent (responsable de l\'inscription)',
      'INSCRIPTION_EN_ATTENTE': 'Email du parent (responsable de l\'inscription)',
      'INSCRIPTION_ANNULEE': 'Email du parent (responsable de l\'inscription)',
      'PAIEMENT_VALIDE': 'Email du parent (responsable de l\'inscription)',
      'PAIEMENT_REFUSE': 'Email du parent (responsable de l\'inscription)',
      'PAIEMENT_RAPPEL': 'Email du parent (responsable de l\'inscription)',
      'DOCUMENT_VALIDE': 'Email du parent (responsable de l\'inscription)',
      'DOCUMENT_REFUSE': 'Email du parent (responsable de l\'inscription)',
      'DOCUMENT_MANQUANT': 'Email du parent (responsable de l\'inscription)',
      'AIDE_VALIDEE': 'Email du parent (responsable de l\'inscription)',
      'AIDE_REFUSEE': 'Email du parent (responsable de l\'inscription)',
      'AIDE_ACCORD': 'Email du parent (responsable de l\'inscription)',
      'AIDE_PAIEMENT': 'Email du parent (responsable de l\'inscription)',
      'PARTENAIRE_VALIDE': 'Email du parent (responsable de l\'inscription)',
      'PARTENAIRE_REFUSE': 'Email du parent (responsable de l\'inscription)',
      'ADMIN_NOUVELLE_INSCRIPTION': 'Emails des administrateurs configurés dans les paramètres',
      'ADMIN_INSCRIPTION_PARTENAIRE': 'Emails des administrateurs configurés dans les paramètres',
      'SYSTEME_BIENVENUE_MEMBRE': 'Email de l\'utilisateur qui vient de créer son compte',
      'SYSTEME_RESET_PASSWORD': 'Email de l\'utilisateur qui demande la réinitialisation',
      'SYSTEME_TEST_SMTP': 'Email spécifié lors du test de configuration',
      'SYSTEME_NOTIFICATION_ADMIN': 'Emails des administrateurs configurés dans les paramètres',
    };

    return destinataireMap[evenement] || 'Non défini';
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation supplémentaire
    if (formData.envoyer_automatiquement && !formData.evenement_declencheur) {
      notify.warning('Veuillez sélectionner un événement déclencheur pour l\'envoi automatique');
      return;
    }

    onSave(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large template-editor">
        <div className="modal-header">
          <h2>{template?.id ? 'Modifier le template' : 'Nouveau template'}</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="editor-layout">
              {/* Formulaire principal */}
              <div className="editor-form">
                <div className="form-group">
                  <label>Nom du template *</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => handleChange('nom', e.target.value)}
                    required
                    placeholder="Ex: Email de confirmation d'inscription"
                  />
                  <small>Donnez un nom unique et descriptif à votre template</small>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    rows="2"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Description de l'utilisation de ce template..."
                  />
                </div>

                <div className="form-group">
                  <label>Sujet de l'email *</label>
                  <div className="input-with-buttons">
                    <input
                      ref={sujetRef}
                      type="text"
                      value={formData.sujet}
                      onChange={(e) => handleChange('sujet', e.target.value)}
                      required
                      placeholder="Ex: Confirmation de votre inscription"
                    />
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => setShowVariables(!showVariables)}
                    >
                      {showVariables ? 'Masquer variables' : 'Afficher variables'}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Corps de l'email *</label>

                  {/* Barre d'outils simple */}
                  <div style={{
                    marginBottom: '8px',
                    padding: '8px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '6px',
                    display: 'flex',
                    gap: '4px',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => applyFormatting('bold')}
                      title="Gras"
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      <strong>B</strong>
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => applyFormatting('italic')}
                      title="Italique"
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      <em>I</em>
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => applyFormatting('underline')}
                      title="Souligné"
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      <u>U</u>
                    </button>
                    <span style={{ borderLeft: '1px solid #d1d5db', margin: '0 4px' }}></span>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => applyFormatting('h2')}
                      title="Titre 2"
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      H2
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => applyFormatting('h3')}
                      title="Titre 3"
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      H3
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => applyFormatting('paragraph')}
                      title="Paragraphe"
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      P
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => applyFormatting('link')}
                      title="Lien"
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      🔗
                    </button>
                    <span style={{ borderLeft: '1px solid #d1d5db', margin: '0 4px' }}></span>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => insertStructure('salutation')}
                      title="Insérer salutation"
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      👋 Bonjour
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => insertStructure('signature')}
                      title="Insérer signature"
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      ✍️ Signature
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => insertStructure('list')}
                      title="Insérer liste"
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      📝 Liste
                    </button>
                  </div>

                  <small style={{ display: 'block', marginBottom: '8px', color: '#6b7280' }}>
                    Sélectionnez du texte puis cliquez sur un bouton pour le formater. Utilisez les variables depuis le panneau à droite.
                  </small>

                  <textarea
                    ref={corpsRef}
                    rows="15"
                    value={formData.corps}
                    onChange={(e) => handleChange('corps', e.target.value)}
                    required
                    placeholder="Écrivez le contenu de votre email ici...&#10;&#10;Astuce : Utilisez la barre d'outils ci-dessus pour formater votre texte et insérer des éléments courants."
                    className="code-textarea"
                    style={{ fontFamily: 'monospace', fontSize: '13px' }}
                  />
                </div>

                <div className="form-group">
                  <label>Emails en copie (CC)</label>
                  <input
                    type="text"
                    value={formData.cc_emails || ''}
                    onChange={(e) => handleChange('cc_emails', e.target.value)}
                    placeholder="admin@example.com, contact@example.com"
                  />
                  <small>Emails qui recevront une copie visible de cet email. Séparez les adresses par des virgules.</small>
                </div>

                <div className="form-group">
                  <label>Emails en copie cachée (BCC/CCI)</label>
                  <input
                    type="text"
                    value={formData.bcc_emails || ''}
                    onChange={(e) => handleChange('bcc_emails', e.target.value)}
                    placeholder="archive@example.com, monitoring@example.com"
                  />
                  <small>Emails qui recevront une copie cachée (invisible pour les autres destinataires). Séparez les adresses par des virgules.</small>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.actif}
                      onChange={(e) => handleChange('actif', e.target.checked)}
                    />
                    Template actif
                  </label>
                </div>

                <div className="form-group checkbox-with-select">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.envoyer_automatiquement}
                      onChange={(e) => handleChange('envoyer_automatiquement', e.target.checked)}
                    />
                    Envoyer automatiquement lors d'un événement
                  </label>
                  <small>Si activé, l'email sera envoyé automatiquement lors de l'événement sélectionné</small>

                  {formData.envoyer_automatiquement && (
                    <div className="conditional-field">
                      <label>Événement déclencheur *</label>
                      <select
                        value={formData.evenement_declencheur || ''}
                        onChange={(e) => handleChange('evenement_declencheur', e.target.value)}
                        required={formData.envoyer_automatiquement}
                      >
                        <option value="">-- Sélectionner un événement --</option>
                        {evenementChoices.map((choice) => (
                          <option key={choice.value} value={choice.value}>
                            {choice.label}
                          </option>
                        ))}
                      </select>

                      {formData.evenement_declencheur && (
                        <>
                          <div className="variables-info" style={{ marginTop: '10px', padding: '12px', backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '6px' }}>
                            <p style={{ margin: 0, fontSize: '13px', color: '#1e40af', lineHeight: '1.5' }}>
                              <strong>ℹ️ À propos de cet événement :</strong><br />
                              {evenementChoices.find(e => e.value === formData.evenement_declencheur)?.description}
                            </p>
                          </div>
                          <div className="variables-info" style={{ marginTop: '10px', padding: '12px', backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '6px' }}>
                            <p style={{ margin: 0, fontSize: '13px', color: '#92400e', lineHeight: '1.5' }}>
                              <strong>📧 Destinataire principal :</strong><br />
                              {getDestinataireInfo(formData.evenement_declencheur)}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Panneau des variables */}
              {showVariables && (
                <div className="variables-panel">
                  <div className="variables-header">
                    <h3>Variables disponibles</h3>
                    <input
                      type="text"
                      placeholder="Rechercher une variable..."
                      value={searchVariable}
                      onChange={(e) => setSearchVariable(e.target.value)}
                      className="search-variables"
                    />
                  </div>

                  <div className="variables-info">
                    <p>Cliquez sur une variable pour l'insérer dans le sujet ou le corps de l'email</p>
                  </div>

                  <div className="variables-list">
                    {Object.entries(filteredVariables).map(([category, vars]) => (
                      <div key={category} className="variable-category">
                        <h4>{category.charAt(0).toUpperCase() + category.slice(1)}</h4>
                        <div className="variable-items">
                          {Object.entries(vars).map(([key, description]) => {
                            const fullVar = `${category}.${key}`;
                            return (
                              <div key={fullVar} className="variable-item">
                                <div className="variable-name">
                                  <code>{`{${fullVar}}`}</code>
                                </div>
                                <div className="variable-description">{description}</div>
                                <div className="variable-actions">
                                  <button
                                    type="button"
                                    className="btn-link btn-sm"
                                    onClick={() => insertVariable(fullVar, 'sujet')}
                                    title="Insérer dans le sujet"
                                  >
                                    Sujet
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-link btn-sm"
                                    onClick={() => insertVariable(fullVar, 'corps')}
                                    title="Insérer dans le corps"
                                  >
                                    Corps
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn-primary">
              Sauvegarder
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateEditor;
